import React from "react";
import { ClipboardList, Trash2, Target, Calendar, PenLine } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { getTargetDirectionSymbol } from "@/lib/treatmentPlan/treatmentPlanData";

interface TreatmentPlanDraftActionItemProps {
  /** The full draft entry from Redux: { data: formData, savedAt: ISO string } */
  draft: { data: any; savedAt: string };
  isDarkMode: boolean;
  /** Open the TreatmentPlanModal with this draft's data */
  onEdit: () => void;
  /** Permanently delete the draft from Redux */
  onDelete: () => void;
}

export const TreatmentPlanDraftActionItem: React.FC<
  TreatmentPlanDraftActionItemProps
> = ({ draft, isDarkMode, onEdit, onDelete }) => {
  const planName = draft?.data?.name?.trim() || "Untitled Plan";
  const description = draft?.data?.description?.trim() || "";
  const goalsCount = Array.isArray(draft?.data?.goals)
    ? draft.data.goals.length
    : 0;
  const appointmentsCount = Array.isArray(draft?.data?.followUpAppointments)
    ? draft.data.followUpAppointments.length
    : 0;

  const savedAt = draft?.savedAt
    ? new Date(draft.savedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const borderCls = isDarkMode ? "border-gray-600" : "border-gray-300";
  const rowHover = isDarkMode ? "hover:bg-gray-700/30" : "hover:bg-gray-50/80";

  return (
    <div
      className={`group relative rounded-lg border-2 border-dashed cursor-pointer transition-all select-none ${
        isDarkMode
          ? "bg-gray-800/40 border-gray-600 hover:bg-gray-800/70"
          : "bg-gray-50/60 border-gray-300 hover:bg-gray-100/80"
      }`}
      onClick={onEdit}
    >
      {/* ── Delete button (hover-reveal, top-right) ─────────────────────── */}
      <div
        className="absolute top-2.5 right-2.5 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              title="Delete draft"
              className={`p-1 rounded transition-colors ${
                isDarkMode
                  ? "hover:bg-red-900/20 text-red-400 hover:text-red-300"
                  : "hover:bg-red-50 text-red-500 hover:text-red-700"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Treatment Plan Draft</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the draft &quot;{planName}
                &quot;? This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Draft
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div
        className={`flex items-center gap-2 px-3 py-2.5 pr-10 border-b border-dashed ${borderCls}`}
      >
        <ClipboardList
          className={`w-4 h-4 flex-shrink-0 ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        />
        <p
          className={`text-sm font-semibold truncate flex-1 ${
            isDarkMode ? "text-gray-200" : "text-gray-700"
          }`}
        >
          {planName}
        </p>
        <Badge className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 shrink-0">
          Draft
        </Badge>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div className="px-3 pb-3">
        {/* Plan overview mini-table */}
        <div
          className={`mt-2.5 overflow-x-auto rounded-md border ${borderCls}`}
        >
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className={isDarkMode ? "bg-gray-700/50" : "bg-gray-100/80"}>
                <th
                  colSpan={2}
                  className={`px-3 py-1.5 text-left text-xs font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Plan Overview
                </th>
              </tr>
            </thead>
            <tbody>
              {description ? (
                <tr className={rowHover}>
                  <Th isDarkMode={isDarkMode}>Description</Th>
                  <Td isDarkMode={isDarkMode}>{description}</Td>
                </tr>
              ) : null}

              {goalsCount > 0 && (
                <tr className={rowHover}>
                  <Th isDarkMode={isDarkMode}>Goals</Th>
                  <Td isDarkMode={isDarkMode}>
                    <span className="flex items-center gap-1.5">
                      <Target className="w-3 h-3 shrink-0" />
                      {goalsCount} goal{goalsCount !== 1 ? "s" : ""}
                    </span>
                  </Td>
                </tr>
              )}

              {appointmentsCount > 0 && (
                <tr className={rowHover}>
                  <Th isDarkMode={isDarkMode}>Follow-up Appointments</Th>
                  <Td isDarkMode={isDarkMode}>
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3 shrink-0" />
                      {appointmentsCount} appointment
                      {appointmentsCount !== 1 ? "s" : ""}
                    </span>
                  </Td>
                </tr>
              )}

              {savedAt && (
                <tr className={rowHover}>
                  <Th isDarkMode={isDarkMode}>Last Saved</Th>
                  <Td isDarkMode={isDarkMode}>{savedAt}</Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Goals preview */}
        {goalsCount > 0 && (
          <div
            className={`mt-2.5 overflow-x-auto rounded-md border ${borderCls}`}
          >
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr
                  className={isDarkMode ? "bg-gray-700/50" : "bg-gray-100/80"}
                >
                  <th
                    colSpan={3}
                    className={`px-3 py-1.5 text-left text-xs font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <Target className="w-3.5 h-3.5" />
                      Clinical Goals
                    </span>
                  </th>
                </tr>
                <tr className={isDarkMode ? "bg-gray-700/40" : "bg-gray-50"}>
                  <Th isDarkMode={isDarkMode}>Goal</Th>
                  <Th isDarkMode={isDarkMode}>Target</Th>
                  <Th isDarkMode={isDarkMode}>Due Date</Th>
                  <Th isDarkMode={isDarkMode}>Status</Th>
                </tr>
              </thead>
              <tbody>
                {(draft.data.goals as any[]).map((g: any, i: number) => (
                  <tr key={g.id ?? i} className={rowHover}>
                    <Td isDarkMode={isDarkMode}>{g.description || "—"}</Td>
                    <Td isDarkMode={isDarkMode}>
                      {g.targetDirection && (
                        <span className="mr-0.5">
                          {getTargetDirectionSymbol(
                            (typeof g.targetDirection === "number"
                              ? (
                                  {
                                    1: "AtOrAbove",
                                    2: "AtOrBelow",
                                    3: "EqualTo",
                                  } as any
                                )[g.targetDirection]
                              : g.targetDirection) as any,
                          )}
                        </span>
                      )}
                      {g.targetValue || "—"}
                    </Td>
                    <Td isDarkMode={isDarkMode}>
                      {g.targetDate
                        ? new Date(g.targetDate).toLocaleDateString()
                        : "—"}
                    </Td>
                    <Td isDarkMode={isDarkMode}>
                      <GoalStatusBadge status={g.status} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Follow-up appointments preview */}
        {appointmentsCount > 0 && (
          <div
            className={`mt-2.5 overflow-x-auto rounded-md border ${borderCls}`}
          >
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr
                  className={isDarkMode ? "bg-gray-700/50" : "bg-gray-100/80"}
                >
                  <th
                    colSpan={3}
                    className={`px-3 py-1.5 text-left text-xs font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      Follow-up Appointment Schedule
                    </span>
                  </th>
                </tr>
                <tr className={isDarkMode ? "bg-gray-700/40" : "bg-gray-50"}>
                  <Th isDarkMode={isDarkMode}>Date</Th>
                  <Th isDarkMode={isDarkMode}>Time</Th>
                  <Th isDarkMode={isDarkMode}>Type</Th>
                </tr>
              </thead>
              <tbody>
                {(draft.data.followUpAppointments as any[]).map(
                  (a: any, i: number) => (
                    <tr key={a.id ?? i} className={rowHover}>
                      <Td isDarkMode={isDarkMode}>
                        {a.date
                          ? new Date(a.date).toLocaleDateString()
                          : a.startDate
                            ? new Date(a.startDate).toLocaleDateString()
                            : "—"}
                      </Td>
                      <Td isDarkMode={isDarkMode}>{a.time || "—"}</Td>
                      <Td isDarkMode={isDarkMode}>
                        {a.serviceType || a.type || "Appointment"}
                      </Td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Continue editing hint */}
        <div
          className={`mt-2.5 flex items-center gap-1.5 text-xs font-medium ${
            isDarkMode ? "text-gray-500" : "text-gray-400"
          }`}
        >
          <PenLine className="w-3 h-3" />
          Click to continue editing
        </div>
      </div>
    </div>
  );
};

/* ── Shared table sub-components ────────────────────────────────────────── */

function Th({
  children,
  isDarkMode,
}: {
  children: React.ReactNode;
  isDarkMode: boolean;
}) {
  return (
    <th
      className={`border px-2 py-1.5 text-left text-xs font-semibold whitespace-nowrap ${
        isDarkMode
          ? "border-gray-600 bg-gray-700/40 text-gray-300"
          : "border-gray-200 bg-gray-50 text-gray-600"
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  isDarkMode,
}: {
  children: React.ReactNode;
  isDarkMode: boolean;
}) {
  return (
    <td
      className={`border px-2 py-1.5 text-xs ${
        isDarkMode
          ? "border-gray-600 text-gray-400"
          : "border-gray-200 text-gray-600"
      }`}
    >
      {children}
    </td>
  );
}

function GoalStatusBadge({ status }: { status?: string }) {
  const map: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    pending: "bg-yellow-100 text-yellow-700",
    "in-progress": "bg-blue-100 text-blue-700",
    achieved: "bg-emerald-100 text-emerald-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700",
    abandoned: "bg-red-100 text-red-700",
  };
  const cls = map[(status ?? "").toLowerCase()] ?? "bg-gray-100 text-gray-600";
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${cls}`}>
      {status || "pending"}
    </span>
  );
}
